import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';

import { DomainEvent } from '../types';
import { formatDiffKey, formatDiffValue } from '../utils/formatters';
import { EventUser } from './UserDisplay';

// ============================================================================
// PRIMITIVES
// ============================================================================

export const EventRow: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  children,
  ...props
}) => (
  <div
    className={cn(
      'inline-flex gap-x-1 gap-y-0.5 text-xs leading-5 items-baseline flex-wrap',
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

export const EventBlock: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  children,
  ...props
}) => (
  <div className={cn('space-y-0.5', className)} {...props}>
    {children}
  </div>
);

export const EventText: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({
  className,
  children,
  ...props
}) => (
  <span className={cn('text-gray-600', className)} {...props}>
    {children}
  </span>
);

export const EventEmphasis: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({
  className,
  children,
  ...props
}) => (
  <span className={cn('font-medium text-gray-900', className)} {...props}>
    {children}
  </span>
);

export const EventMuted: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({
  className,
  children,
  ...props
}) => (
  <span className={cn('text-gray-400 text-[10px]', className)} {...props}>
    {children}
  </span>
);

interface StatusBadgeProps {
  status: string;
  variant?: 'from' | 'to' | 'default';
  size?: 'xs' | 'sm';
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  variant = 'default',
  size = 'xs',
}) => {
  const s = status?.toLowerCase() || '';
  let colorClasses = 'bg-gray-50 text-gray-600 ring-gray-200/80';

  if (variant === 'from') {
    colorClasses =
      'bg-gray-50 text-gray-500 ring-gray-200/60 line-through decoration-gray-400';
  } else if (
    [
      'completed',
      'done',
      'satisfied',
      'signed',
      'approved',
      'verified_received',
    ].includes(s)
  ) {
    colorClasses = 'bg-emerald-50 text-emerald-700 ring-emerald-600/20';
  } else if (
    ['in_progress', 'in progress', 'active', 'working', 'treating'].includes(s)
  ) {
    colorClasses = 'bg-blue-50 text-blue-700 ring-blue-600/20';
  } else if (
    ['pending', 'waiting', 'review', 'draft', 'needs_approval'].includes(s)
  ) {
    colorClasses = 'bg-amber-50 text-amber-700 ring-amber-600/20';
  } else if (['cancelled', 'deleted', 'failed', 'rejected'].includes(s)) {
    colorClasses = 'bg-red-50 text-red-700 ring-red-600/20';
  }

  const sizeClasses =
    size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs';

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md font-medium ring-1 ring-inset',
        colorClasses,
        sizeClasses,
      )}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
};

interface TagProps {
  children: React.ReactNode;
  color?: 'gray' | 'blue' | 'purple' | 'emerald' | 'amber' | 'red';
  size?: 'xs' | 'sm';
}

export const Tag: React.FC<TagProps> = ({
  children,
  color = 'gray',
  size = 'xs',
}) => {
  const colorMap = {
    gray: 'bg-gray-50 text-gray-700 ring-gray-600/10',
    blue: 'bg-blue-50 text-blue-700 ring-blue-600/10',
    purple: 'bg-purple-50 text-purple-700 ring-purple-600/10',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-600/10',
    amber: 'bg-amber-50 text-amber-700 ring-amber-600/10',
    red: 'bg-red-50 text-red-700 ring-red-600/10',
  };

  const sizeClasses =
    size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-x-1 rounded-md font-medium ring-1 ring-inset',
        colorMap[color],
        sizeClasses,
      )}
    >
      {children}
    </span>
  );
};

// ============================================================================
// COLLAPSIBLE DETAILS
// ============================================================================

interface CollapsibleDetailsProps {
  summary: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export const CollapsibleDetails = React.memo<CollapsibleDetailsProps>(
  ({ summary, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const handleToggle = (e: React.MouseEvent) => {
      // Prevent toggle when clicking links or interactive elements inside summary
      const target = e.target as HTMLElement;
      if (target.closest('a') || target.closest('button')) {
        return;
      }
      setIsOpen(!isOpen);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
    };

    if (!children) {
      return (
        <React.Fragment key="collapsible-details">{summary}</React.Fragment>
      );
    }

    return (
      <div className="space-y-1 group/details">
        <div
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          className="flex items-start gap-1 cursor-pointer -ml-1.5 pl-1.5 pr-1 py-0.5 rounded-md hover:bg-gray-50 transition-colors select-none focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          <div className="flex-1 min-w-0">{summary}</div>
          <div
            className={cn(
              'mt-1 p-0.5 text-gray-400 hover:text-gray-600 transition-colors duration-200',
              // Show chevron on hover or if open, otherwise keep it subtle
              'opacity-0 group-hover/details:opacity-100',
              isOpen && 'opacity-100',
            )}
          >
            {isOpen ? (
              <ChevronUpIcon className="w-3 h-3" />
            ) : (
              <ChevronDownIcon className="w-3 h-3" />
            )}
          </div>
        </div>
        {isOpen && (
          <div className="pl-3 border-l-2 border-gray-100 ml-1.5 mt-1 text-xs text-gray-600 animate-in fade-in slide-in-from-top-1 duration-200">
            {children}
          </div>
        )}
      </div>
    );
  },
);

CollapsibleDetails.displayName = 'CollapsibleDetails';

// ============================================================================
// PATTERNS
// ============================================================================

export const UserActionPattern: React.FC<{
  event: DomainEvent;
  action: string;
  children?: React.ReactNode;
  details?: React.ReactNode;
}> = ({ event, action, children, details }) => {
  const content = (
    <EventRow>
      <EventUser event={event} />
      <EventText>{action}</EventText>
      {children}
    </EventRow>
  );

  if (details) {
    return <CollapsibleDetails summary={content}>{details}</CollapsibleDetails>;
  }

  return content;
};

export const CreatedEntityPattern: React.FC<{
  event: DomainEvent;
  entityType: string;
  entityName?: React.ReactNode;
  tag?: { text: string; color?: 'blue' | 'purple' | 'emerald' | 'amber' };
  details?: React.ReactNode;
}> = ({ event, entityType, entityName, tag, details }) => {
  const content = (
    <EventRow>
      <EventUser event={event} />
      <EventText>created {entityType}</EventText>
      {entityName}
      {tag && (
        <Tag color={tag.color || 'blue'} size="xs">
          {tag.text}
        </Tag>
      )}
    </EventRow>
  );

  if (details) {
    return <CollapsibleDetails summary={content}>{details}</CollapsibleDetails>;
  }
  return content;
};

export const StatusChangePattern: React.FC<{
  event: DomainEvent;
  entityType: string;
  entityName?: React.ReactNode;
  oldStatus: string | null | undefined;
  newStatus: string | null | undefined;
  customVerb?: string;
  details?: React.ReactNode;
}> = ({
  event,
  entityType,
  entityName,
  oldStatus,
  newStatus,
  customVerb,
  details,
}) => {
  if (!newStatus) return null;

  const getStatusVerb = (oldS: string | null, newS: string): string => {
    const normalized = newS?.toLowerCase();
    if (['completed', 'done', 'satisfied', 'signed'].includes(normalized))
      return 'completed';
    if (['assigned', 'in_progress', 'active'].includes(normalized))
      return 'started';
    if (['cancelled', 'deleted', 'failed'].includes(normalized))
      return 'cancelled';
    if (['approved'].includes(normalized)) return 'approved';
    return 'updated';
  };

  const verb = customVerb || getStatusVerb(oldStatus || null, newStatus);

  const content = (
    <EventBlock>
      <EventRow>
        <EventUser event={event} />
        <EventText>{verb}</EventText>
        {entityName || <EventText>{entityType}</EventText>}
      </EventRow>
      {(oldStatus || newStatus) && (
        <EventRow className="items-center mt-1.5 w-full">
          {oldStatus && (
            <>
              <StatusBadge status={oldStatus} variant="from" />
              <EventMuted className="mx-1">→</EventMuted>
            </>
          )}
          <StatusBadge status={newStatus} variant="to" />
        </EventRow>
      )}
    </EventBlock>
  );

  if (details) {
    return <CollapsibleDetails summary={content}>{details}</CollapsibleDetails>;
  }

  return content;
};

export const AssignmentPattern: React.FC<{
  event: DomainEvent;
  entityName?: React.ReactNode;
  assignedTo?: string;
  unassigned?: boolean;
}> = ({ event, entityName, assignedTo, unassigned }) => (
  <EventRow>
    <EventUser event={event} />
    <EventText>{unassigned ? 'unassigned' : 'assigned'}</EventText>
    {entityName}
    {assignedTo && !unassigned && (
      <>
        <EventText>to</EventText>
        <EventText className="font-medium text-gray-900">
          {assignedTo}
        </EventText>
      </>
    )}
  </EventRow>
);

export const DeletionPattern: React.FC<{
  event: DomainEvent;
  entityType: string;
  entityName?: string;
  verb?: string;
}> = ({ event, entityType, entityName, verb = 'removed' }) => (
  <EventRow>
    <EventUser event={event} />
    <EventText>{verb}</EventText>
    <EventText className="font-medium text-gray-900 line-through opacity-60">
      {entityName || entityType}
    </EventText>
  </EventRow>
);

export const ChangesSummary: React.FC<{
  diff: Record<string, unknown>;
}> = ({ diff }) => {
  // Normalize DeepDiff structure or simple diff structure into a flat list
  const changes: Array<{ key: string; oldVal: unknown; newVal: unknown }> = [];

  const deepDiffKeys = [
    'values_changed',
    'dictionary_item_added',
    'dictionary_item_removed',
    'iterable_item_added',
    'iterable_item_removed',
    'type_changes',
  ];

  const isDeepDiff = Object.keys(diff).some((k) => deepDiffKeys.includes(k));

  if (isDeepDiff) {
    // Handle DeepDiff structure
    if (diff.values_changed) {
      Object.entries(diff.values_changed as Record<string, unknown>).forEach(
        ([key, val]) => {
          const typedVal = val as { old_value: unknown; new_value: unknown };
          changes.push({
            key,
            oldVal: typedVal.old_value,
            newVal: typedVal.new_value,
          });
        },
      );
    }
    if (diff.dictionary_item_added) {
      Object.entries(
        diff.dictionary_item_added as Record<string, unknown>,
      ).forEach(([key, val]) => {
        changes.push({
          key,
          oldVal: undefined,
          newVal: val,
        });
      });
    }
    if (diff.dictionary_item_removed) {
      Object.entries(
        diff.dictionary_item_removed as Record<string, unknown>,
      ).forEach(([key, val]) => {
        changes.push({
          key,
          oldVal: val,
          newVal: undefined,
        });
      });
    }
    // Handle other deepdiff types if necessary, currently sticking to common ones
  } else {
    // Handle simple flat diff: { field: { old: 'a', new: 'b' } } or { field: 'value' }
    Object.keys(diff).forEach((key) => {
      const rawVal = diff[key];
      let oldVal: unknown;
      let newVal: unknown;
      let hasOldNew = false;

      if (rawVal && typeof rawVal === 'object') {
        const v = rawVal as Record<string, unknown>;
        if ('new_value' in v || 'old_value' in v) {
          newVal = v.new_value;
          oldVal = v.old_value;
          hasOldNew = true;
        } else if ('new' in v || 'old' in v) {
          newVal = v.new;
          oldVal = v.old;
          hasOldNew = true;
        }
      }

      if (hasOldNew) {
        changes.push({ key, oldVal, newVal });
      } else {
        // Just a value (assumed new)
        changes.push({ key, oldVal: undefined, newVal: rawVal });
      }
    });
  }

  if (changes.length === 0) return null;

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {changes.map(({ key, oldVal, newVal }) => {
        const label = formatDiffKey(key);
        // Determine if we have both values to show arrow
        const showArrow =
          oldVal !== undefined &&
          oldVal !== null &&
          newVal !== undefined &&
          newVal !== null;

        // If simple diff mode fell through to just a value, or added/removed
        const isAddition =
          (oldVal === undefined || oldVal === null) &&
          newVal !== undefined &&
          newVal !== null;
        const isRemoval =
          (newVal === undefined || newVal === null) &&
          oldVal !== undefined &&
          oldVal !== null;

        let diffContent: React.ReactNode;

        if (showArrow) {
          diffContent = (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="text-red-600 bg-red-50 px-1 rounded line-through decoration-red-300/50 break-all decoration-1">
                {formatDiffValue(oldVal)}
              </span>
              <span className="text-gray-300 text-[10px] flex-shrink-0 select-none">
                →
              </span>
              <span className="text-emerald-700 bg-emerald-50 px-1 rounded font-medium break-all">
                {formatDiffValue(newVal)}
              </span>
            </div>
          );
        } else if (isAddition) {
          diffContent = (
            <span className="text-emerald-700 bg-emerald-50 px-1 rounded font-medium break-all">
              {formatDiffValue(newVal)}
            </span>
          );
        } else if (isRemoval) {
          diffContent = (
            <span className="text-red-600 bg-red-50 px-1 rounded line-through decoration-red-300/50 break-all decoration-1">
              {formatDiffValue(oldVal)}
            </span>
          );
        } else {
          diffContent = (
            <span className="text-gray-900 break-words font-medium">
              {formatDiffValue(newVal)}
            </span>
          );
        }

        return (
          <div
            key={key}
            className="text-xs grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 items-baseline group/diff"
          >
            <div className="font-medium text-gray-500 whitespace-nowrap min-w-[80px]">
              {label}:
            </div>

            <div className="min-w-0">{diffContent}</div>
          </div>
        );
      })}
    </div>
  );
};
